





















































































































































import { Component, Vue, Watch } from 'vue-property-decorator'
import { ReferralChannel, ReferralNodeType, ReferralStatus, ReferralType } from '@/api/types'
import { InputSelectOption } from '@/helpers/options'
import { CompanyAPI, SourceAPI, WebhookAPI } from '@/api/backend-client'
import { Webhook } from '@/api/model/webhook'
import { Company } from '@/api/model/company'
import { Branch } from '@/api/model/branch'
import { ReferralNode } from '@/api/model/referral-node'
import { v4 as uuid } from 'uuid'
import { Source } from '@/api/model/source'
import { Pipeline } from '@/api/model/pipeline'
import { usePipelineConnections } from '@/composables/usePipelineConnections'

const pipelineConnections = usePipelineConnections()

@Component
export default class CompanySettingsWebhookCreate extends Vue {
    loading: boolean = false
    name: string = null
    referralType: ReferralType = null
    pipeline: string = null
    assignedToCompany: Company = null
    assignedToBranch: Branch = null
    status: string = null
    score: number = 0
    priority: boolean = false
    source: string = null
    newSource: string = null
    additionalComment: string = null
    availableBranches: Branch[] = []
    availableSources: Source[] = []

    async mounted(): Promise<void> {
        this.scrollToTop()
        await this.reloadAvailableBranches()
        await this.reloadAvailableSources()
    }

    scrollToTop(): void {
        document.querySelector('.main').scrollTo({ top: 0 })
    }

    async save(): Promise<void> {
        this.loading = true
        this.scrollToTop()
        await WebhookAPI.create(Webhook.fromData({
            company: this.$currentCompany.resourceIdentifier,
            id: uuid(),
            name: this.name,
            referralType: this.referralType,
            pipeline: this.selectedPipeline?.resourceIdentifier,
            assignedToCompany: this.assignedToCompany?.resourceIdentifier,
            assignedToBranch: this.assignedToBranch,
            status: this.status,
            score: this.score,
            priority: this.priority,
            source: this.source,
            newSource: this.newSource ? this.availableSources.find(source => source.id === this.newSource) : null,
            additionalComment: this.additionalComment,
        }))
        this.loading = false
        await this.backToLister()
    }

    async backToLister(): Promise<void> {
        await this.$router.push({
            name: 'company_settings_smart_marketing_webhooks',
        })
    }

    @Watch('pipelineOptions')
    onPipelineOptionsChanged(): void {
        if (this.pipelineOptions.length === 1) {
            this.pipeline = this.pipelineOptions[0].value as string
        }
    }

    @Watch('companyOptions')
    onCompanyOptionsChanged(): void {
        if (this.companyOptions.length === 1) {
            this.assignedToCompany = this.companyOptions[0]
        }
    }

    @Watch('branchOptions')
    onBranchOptionsChanged(): void {
        if (this.branchOptions.length === 1) {
            this.assignedToBranch = this.branchOptions[0]
        }
    }

    @Watch('referralType')
    async reloadAvailableBranches(): Promise<void> {
        this.$currentCompany.ensureOutgoingReferralsAreLoaded()

        this.availableBranches = (await Promise.all(this.$currentCompany.outgoingReferrals

            // Keep only active referral nodes.
            .filter((node: ReferralNode) => node.active === true)

            // Keep only desired referral type nodes.
            .filter((node: ReferralNode) => node.type === this.referralType as unknown as ReferralNodeType)

            // Create a map of branches.
            .map(async (node: ReferralNode) => {
                const company = await CompanyAPI.get(node.target.id)
                return company.getAvailableBranches()

                    // Keep branches that accept the referral type.
                    .filter((branch: Branch) => branch.acceptsReferralType(this.referralType))

                    // Sort alphabetically.
                    .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
            })))

            // Flatten promise.all results.
            .flat() as Branch[]
    }

    @Watch('assignedToCompany')
    async reloadAvailableSources(): Promise<void> {
        if (this.assignedToCompany === null) {
            this.availableSources.splice(0)
            return
        }

        const sources = (await SourceAPI.getAll({ company: this.assignedToCompany.id, active: 1 }))

        this.availableSources.splice(0)
        this.availableSources.push(...sources)
    }

    get assignableCompanies(): Company[] {
        const assignableCompanies = new Map<string, Company>()
        this.availableBranches.forEach((branch: Branch) => {
            assignableCompanies.set(branch.company.id, branch.company)
        })

        if (this.availableBranches.length === 0) {
            assignableCompanies.set(this.$currentCompany.id, this.$currentCompany)
        }

        return Array.from(assignableCompanies.values()).sort((a: Company, b: Company) => a.name.localeCompare(b.name))
    }

    get assignableBranches(): Branch[] {
        const assignableBranches = new Map<string, Branch>()
        this.availableBranches.forEach((branch: Branch) => {
            if (branch.company.id === this.assignedToCompany.id) {
                assignableBranches.set(branch.id, branch)
            }
        })

        return Array.from(assignableBranches.values())
            .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
    }

    get availableStatuses(): InputSelectOption[] {
        if (this.$currentCompany.pipelinesEnabled) {
            return [ReferralStatus.OPEN, ReferralStatus.PROCESSED].map(status => ({
                label: this.$t('referral.phaseModelStatus.' + status).toString(),
                value: status,
            }))
        } else {
            return [ReferralStatus.NEW, ReferralStatus.PROCESSED].map(status => ({
                label: this.$t('referral.status.' + status).toString(),
                value: status,
            }))
        }
    }

    get availableReferralTypes(): InputSelectOption[] {
        return this.$currentCompany.handledReferralTypes
            .map(type => ({
                label: this.$t('referral.type.' + type).toString(),
                value: type,
            }))
            .sort((a, b) => {
                return a.label.localeCompare(b.label)
            })
    }

    get availablePriorities(): InputSelectOption[] {
        return [false, true].map(priority => ({
            label: this.$t('webhook.priorities.' + (priority ? 'yes' : 'no')).toString(),
            value: priority,
        }))
    }

    get availableSourceOptions(): InputSelectOption[] {
        return this.availableSources
            .filter(source => source.channel === ReferralChannel.ONLINE)
            .map(source => ({
                label: source.name,
                value: source.id,
            }))
    }

    get pipelinesEnabled(): boolean {
        return this.assignedToCompany?.pipelinesEnabled
    }

    get selectedPipeline(): Pipeline {
        return this.outgoingPipelines
            .find(pipeline => pipeline.id === this.pipeline)
    }

    get outgoingPipelines(): Pipeline[] {
        return pipelineConnections
            .buildOutgoingPipelines()
    }

    get pipelineOptions(): InputSelectOption[] {
        return this.outgoingPipelines
            .filter((pipeline: Pipeline) => pipeline.company?.id === this.assignedToCompany?.id)
            .map(pipeline => ({
                label: pipeline.name,
                value: pipeline.id,
                subtext: pipeline.company?.name,
            }))
    }

    get companyOptions(): Company[] {
        const companies = new Map<string, Company>()

        // First fill map with referral type companies.
        this.assignableCompanies.forEach((company: Company) => {
            companies.set(company.id, company)
        })

        // Then fill with pipeline companies (overwriting referral type companies).
        pipelineConnections.buildOutgoingCompanies().forEach((company: Company) => {
            companies.set(company.id, company)
        })

        return Array.from(companies.values())
            .sort((a: Company, b: Company) => a.name.localeCompare(b.name))
    }

    get branchOptions(): Branch[] {

        // legacy support
        if (this.pipelinesEnabled === false) {
            return this.assignableBranches
        }

        if (this.pipeline) {
            if (this.selectedPipeline) {
                return this.selectedPipeline.branches
                    .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
            }
        }

        return []
    }
}
